import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import { useT } from "@/lib/i18n";

export const LangToggle = ({ dark = false }) => {
  const { lang, setLang } = useT();
  return (
    <div className={`flex overflow-hidden rounded-full border text-xs font-bold ${dark ? "border-white/20" : "border-slate-200"}`} data-testid="lang-toggle">
      {["en", "fr"].map((l) => <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`} className={`px-2.5 py-1.5 uppercase transition-colors ${lang === l ? "bg-[#0B1120] text-white" : dark ? "text-slate-300 hover:bg-white/10" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>)}
    </div>
  );
};

const NAV = [
  ["Home", "/"], ["Football", "/football"], ["Basketball", "/basketball"], ["Athletics", "/athletics"],
  ["Combat", "/combat"], ["Handball", "/handball"], ["More", "/more"],
];

export const Logo = ({ className = "h-14" }) => (
  <Link to="/" data-testid="nav-logo" className="flex items-center">
    <img src="/logo.png" alt="MiniSports — Cameroon in Motion" className={`${className} w-auto`} />
  </Link>
);

export const Header = () => {
  const { t } = useT();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const nav = useNavigate();
  const submit = (e) => { e.preventDefault(); if (q.trim()) { nav(`/search?q=${encodeURIComponent(q.trim())}`); setSearching(false); setOpen(false); } };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-100" data-testid="site-header">
      <div className="container-x flex h-[76px] items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7" data-testid="main-nav">
          {NAV.map(([label, to]) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) => `relative py-1 text-[15px] font-semibold transition-colors ${isActive ? "text-[#047857] after:absolute after:left-0 after:-bottom-1 after:h-[3px] after:w-full after:rounded-full after:bg-[#047857]" : "text-slate-700 hover:text-[#047857]"}`}>
              {t(label)}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle />
          {searching ? (
            <form onSubmit={submit} className="flex items-center rounded-full border border-slate-200 pl-3">
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search athletes, news, teams…")} data-testid="header-search-input"
                className="h-10 w-48 sm:w-64 bg-transparent text-sm outline-none" />
              <button type="button" onClick={() => setSearching(false)} className="p-2 text-slate-500" aria-label="Close search"><X size={16} /></button>
            </form>
          ) : (
            <button onClick={() => setSearching(true)} data-testid="header-search-btn" aria-label="Search" className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50">
              <Search size={18} />
            </button>
          )}
          <Link to="/live-scores" data-testid="nav-live-scores-btn" className="btn-green hidden sm:inline-flex"><span className="live-dot" /> {t("Live Scores")}</Link>
          <button className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-slate-200" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white px-4 py-4" data-testid="mobile-menu">
          <div className="grid grid-cols-2 gap-2">
            {[...NAV, ["Women", "/women"], ["Live Scores", "/live-scores"], ["Videos", "/videos"], ["Abroad", "/cameroonians-abroad"]].map(([label, to]) => (
              <Link key={to} to={to} onClick={() => setOpen(false)} className="rounded-lg bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">{t(label)}</Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
